import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { Avatar, Row, Col, Alert, Select } from "antd";
import { SelectValue } from "antd/lib/select";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

// components
import * as Layout from "common/layout";
import Text from "common/text";
import GET_TEACHER_DETAILS from "graphql/queries/GetTeacherDetails";
import GET_TEACHER_LEVELS from "graphql/queries/GetTeacherLevels";

// custom hooks
import useBannerBackground from "hooks/useBannerBackground";

// custom types
import { Level } from "types";

import * as Styled from "./TeacherProfileStyles";
import JoinMyClasses from "./JoinMyClasses";

const { Option } = Select;

const TeacherProfile = () => {
  const { t } = useTranslation("teacher_profile");
  const params = useParams();
  const [levelId, setLevelId] = useState("0");
  const { teacherId } = params;

  const { data: teacherLevels } = useQuery(GET_TEACHER_LEVELS, {
    variables: {
      teacherId,
    },
  });

  const { data, loading, error } = useQuery(GET_TEACHER_DETAILS, {
    variables: {
      id: teacherId,
    },
  });
  const { backgroundImage } = useBannerBackground();

  const onChangeLevel = (value: string) => {
    setLevelId(value);
  };

  if (loading) {
    return (
      <Alert style={{ marginTop: "64px" }} type="info" message="Loading..." />
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        style={{ marginTop: "64px" }}
        message="Error loading teacher profile. Please check your internet or let us know if this keeps happening."
      />
    );
  }

  const { teacherDetails: teacher } = data;

  return (
    <Layout.PageContent>
      <Layout.HeadingWrapper style={backgroundImage}>
        <Styled.HeadingWrapper style={{ backgroundColor: "transparent" }}>
          <Styled.TeacherNameWrapper>
            <Styled.TeacherChineseNameHeading>
              {teacher.nonRomanFamilyName}
            </Styled.TeacherChineseNameHeading>
            <Styled.TeacherNameHeading>
              {teacher.familyName}
            </Styled.TeacherNameHeading>
          </Styled.TeacherNameWrapper>
          <Avatar size={100} src={teacher.avatar} />
        </Styled.HeadingWrapper>
      </Layout.HeadingWrapper>

      <Styled.Content>
        {/* Profile section */}
        <Text.H1>{t("TEACHER PROFILE").toUpperCase()}</Text.H1>
        <Row>
          <Col md={8} sm={24} xs={24}>
            <Styled.Card>
              <Styled.CardTitle>
                {t("QUICK FACTS").toUpperCase()}
              </Styled.CardTitle>
              <Styled.CardContent>
                <Styled.FactList>
                  {teacher.quickFacts.map((fact: string) => (
                    <li key={fact}>{fact}</li>
                  ))}
                </Styled.FactList>
              </Styled.CardContent>
            </Styled.Card>
          </Col>
          <Col md={8} sm={24} xs={24}>
            <Styled.Card>
              <Styled.CardTitle>{t("ABOUT ME").toUpperCase()}</Styled.CardTitle>
              <Styled.CardContent>{teacher.aboutMe}</Styled.CardContent>
            </Styled.Card>
          </Col>
          <Col md={8} sm={24} xs={24}>
            <Styled.Card>
              <Styled.CardTitle>
                {t("MY STUDYING TIPS").toUpperCase()}
              </Styled.CardTitle>
              <Styled.CardContent>{teacher.studyingTips}</Styled.CardContent>
            </Styled.Card>
          </Col>
        </Row>
        {/* End of Profile section */}
        <br />
        {/* Join my classes section */}

        <Styled.FilterLevelsRow>
          <Col md={18} sm={24} xs={24}>
            <Text.H1>{t("JOIN MY CLASSES").toUpperCase()}</Text.H1>
          </Col>
          <Col md={6} sm={24} xs={24}>
            <Styled.LevelSelect
              optionFilterProp="children"
              defaultValue="0"
              getPopupContainer={(trigger) => trigger.parentNode}
              onChange={(value: SelectValue) => {
                onChangeLevel(value as string);
              }}
            >
              <Option value="0" key="0">
                Show All Levels
              </Option>
              {teacherLevels?.teacherLevels.map((level: Level) => (
                <Option value={level.id} key={level.title}>
                  {level.title}
                </Option>
              ))}
            </Styled.LevelSelect>
          </Col>
        </Styled.FilterLevelsRow>

        <JoinMyClasses teacherId={Number(teacherId || "")} levelId={levelId} />
        {/* End of my classes section */}
      </Styled.Content>
    </Layout.PageContent>
  );
};

export default TeacherProfile;
